import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageProvider";
import { ModOverview, ModPostRef, useForumApi } from "../forum/forumApi";

// Moderator overview: what needs attention in the forum, read-only. Moderation
// itself happens in the forum (post menus), so every item links to its post.
export const ForumTab = () => {
  const { t, locale } = useLanguage();
  const forum = useForumApi();
  const [data, setData] = useState<ModOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    forum
      .modOverview()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [forum]);

  const when = (iso: string) => new Date(iso).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  const excerpt = (s: string) => (s.length > 220 ? `${s.slice(0, 220)}…` : s);
  const postLink = (r: { threadId: string | null; postId: string | null }) =>
    r.threadId ? `/forum/t/${r.threadId}${r.postId ? `#p-${r.postId}` : ""}` : "/forum";

  const PostRow: React.FC<{ r: ModPostRef; meta: React.ReactNode }> = ({ r, meta }) => (
    <tr>
      <td>
        <Link to={postLink(r)} style={{ fontWeight: 600 }}>
          {r.threadTitle}
        </Link>
        <div style={{ whiteSpace: "pre-wrap", margin: "4px 0", fontSize: "0.88rem" }}>{excerpt(r.body)}</div>
        <div style={{ fontSize: "0.78rem", color: "var(--sp-text-dim)" }}>
          {r.authorName} · {meta}
        </div>
      </td>
      <td style={{ textAlign: "right", verticalAlign: "top", whiteSpace: "nowrap" }}>
        <Link to={postLink(r)} className="sp-btn sp-btn--ghost sp-btn--sm">
          {t("admin.forum.open")} →
        </Link>
      </td>
    </tr>
  );

  if (error) return <div className="sp-empty">{error}</div>;
  if (!data) {
    return (
      <div className="sp-empty">
        <span className="sp-spinner" />
      </div>
    );
  }

  const c = data.counts;
  const tiles: { label: string; value: number; sub?: string; warn?: boolean }[] = [
    { label: t("admin.forum.openReports"), value: c.openReports, warn: c.openReports > 0 },
    { label: t("admin.forum.hiddenPosts"), value: c.hiddenPosts, sub: t("admin.forum.hiddenThreads", { n: c.hiddenThreads }) },
    { label: t("admin.forum.bannedMembers"), value: c.banned },
    { label: t("admin.forum.postsWeek"), value: c.postsWeek, sub: t("admin.forum.postsDay", { n: c.postsDay }) },
    { label: t("admin.forum.threadsWeek"), value: c.threadsWeek, sub: t("admin.forum.newMembers", { n: c.newMembersWeek }) },
  ];

  return (
    <>
      <div className="sp-card" style={{ marginBottom: 16 }}>
        <div className="sp-card__head">
          <div>
            <h3 className="sp-card__title">{t("admin.forum.overviewTitle")}</h3>
            <div className="sp-card__sub">{t("admin.forum.overviewLead")}</div>
          </div>
          <Link to="/forum" className="sp-btn sp-btn--primary sp-btn--sm">
            {t("admin.forum.toForum")} →
          </Link>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          {tiles.map((tile) => (
            <div key={tile.label} className="sp-tile" style={tile.warn ? { borderColor: "var(--color-warning)" } : undefined}>
              <div className="sp-tile__label">{tile.label}</div>
              <div className="sp-tile__value">{tile.value}</div>
              {tile.sub && <div className="sp-tile__sub">{tile.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="sp-card" style={{ marginBottom: 16 }}>
        <div className="sp-card__head">
          <h3 className="sp-card__title">
            {t("admin.forum.reports")} <span className="sp-chip">{data.reports.length}</span>
          </h3>
        </div>
        {data.reports.length === 0 ? (
          <div className="sp-empty">{t("admin.forum.noReports")}</div>
        ) : (
          <table className="sp-table" style={{ width: "100%" }}>
            <tbody>
              {data.reports.map((r) => (
                <PostRow
                  key={r.postId}
                  r={r}
                  meta={
                    <>
                      <span className="sp-chip sp-chip--warn">{r.reports === 1 ? t("admin.forum.report1") : t("admin.forum.reportCount", { n: r.reports })}</span>{" "}
                      {r.hidden && <span className="sp-chip">{t("admin.forum.alreadyHidden")}</span>}{" "}
                      {r.reasons.length > 0 && <>„{r.reasons.slice(0, 2).join("”, „")}” · </>}
                      {when(r.lastReport)}
                    </>
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="sp-card" style={{ marginBottom: 16 }}>
        <div className="sp-card__head">
          <h3 className="sp-card__title">
            {t("admin.forum.hiddenTitle")} <span className="sp-chip">{data.hidden.length}</span>
          </h3>
        </div>
        {data.hidden.length === 0 ? (
          <div className="sp-empty">{t("admin.forum.noHidden")}</div>
        ) : (
          <table className="sp-table" style={{ width: "100%" }}>
            <tbody>
              {data.hidden.map((r) => (
                <PostRow
                  key={r.postId}
                  r={r}
                  meta={t("admin.forum.hiddenBy", { name: r.hiddenBy ?? "–", date: when(r.hiddenAt) })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="sp-card" style={{ marginBottom: 16 }}>
        <div className="sp-card__head">
          <h3 className="sp-card__title">
            {t("admin.forum.bannedTitle")} <span className="sp-chip">{data.banned.length}</span>
          </h3>
        </div>
        {data.banned.length === 0 ? (
          <div className="sp-empty">{t("admin.forum.noBanned")}</div>
        ) : (
          <table className="sp-table" style={{ width: "100%" }}>
            <tbody>
              {data.banned.map((b) => (
                <tr key={`${b.name}-${b.bannedAt}`}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{b.name ?? "–"}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--sp-text-dim)" }}>
                      {t("admin.forum.bannedSince", { date: when(b.bannedAt) })} · {t("admin.forum.postCount", { n: b.posts })}
                    </div>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {b.postId && (
                      <Link to={postLink(b)} className="sp-btn sp-btn--ghost sp-btn--sm">
                        {t("admin.forum.latestPost")} →
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="sp-card">
        <div className="sp-card__head">
          <div>
            <h3 className="sp-card__title">{t("admin.forum.editedTitle")}</h3>
            <div className="sp-card__sub">{t("admin.forum.editedLead")}</div>
          </div>
        </div>
        {data.edited.length === 0 ? (
          <div className="sp-empty">{t("admin.forum.noEdited")}</div>
        ) : (
          <table className="sp-table" style={{ width: "100%" }}>
            <tbody>
              {data.edited.map((r) => (
                <PostRow
                  key={r.postId}
                  r={r}
                  meta={t("admin.forum.editedMeta", { date: when(r.editedAt), n: r.revisions })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};
